import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAllTournamentGroups } from "@/hooks/useAllTournamentGroups";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, MapPin } from "lucide-react";

interface AllGroupsViewProps {
  eventId: string;
  scheduleData: any;
}

export function AllGroupsView({ eventId, scheduleData }: AllGroupsViewProps) {
  const { groups, loading } = useAllTournamentGroups(eventId);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          No hay grupos disponibles todavía
        </p>
      </Card>
    );
  }

  const currentGroup = selectedGroup || groups[0]?.groupName;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">Todos los Grupos</h3>
        <p className="text-sm text-muted-foreground">
          Explora los grupos y partidos del torneo
        </p>
      </div>

      <Tabs value={currentGroup} onValueChange={setSelectedGroup}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {groups.map((group) => (
            <TabsTrigger key={group.groupName} value={group.groupName}>
              Grupo {group.groupName}
              <span className="ml-2 text-xs opacity-70">
                ({group.participantCount})
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {groups.map((group) => (
          <TabsContent key={group.groupName} value={group.groupName}>
            <div className="space-y-6">
              {/* Group Header */}
              <div>
                <h4 className="text-lg font-semibold">Grupo {group.groupName}</h4>
                <p className="text-sm text-muted-foreground">
                  {group.category}
                  {group.subcategory && ` - ${group.subcategory}`}
                </p>
                {group.scheduleInfo && (
                  <div className="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
                    {group.scheduleInfo.date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{group.scheduleInfo.date}</span>
                      </div>
                    )}
                    {group.scheduleInfo.startTime && group.scheduleInfo.endTime && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{group.scheduleInfo.startTime} - {group.scheduleInfo.endTime}</span>
                      </div>
                    )}
                    {(group.scheduleInfo.court || group.scheduleInfo.courtHidden) && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{group.scheduleInfo.courtHidden ? 'Cancha por definir' : `Cancha ${group.scheduleInfo.court}`}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Participants */}
              {group.participants.length > 0 && (
                <Card className="p-4">
                  <h5 className="font-semibold mb-4">Participantes</h5>
                  <div className="space-y-3">
                    {group.participants.map((participant) => (
                      <div 
                        key={participant.id} 
                        className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                      >
                        <Badge variant="secondary" className="text-base">
                          {participant.letter}
                        </Badge>
                        <div>
                          <p className="font-medium">
                            {participant.player1}
                            {participant.player2 && ` / ${participant.player2}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Matches */}
              {group.matches.length > 0 ? (
                <Card className="p-4">
                  <h5 className="font-semibold mb-4">Partidos Programados</h5>
                  <div className="space-y-3">
                    {group.matches.map((match) => (
                      <div 
                        key={match.id} 
                        className="p-4 rounded-lg border bg-card space-y-2"
                      >
                        <div className="font-medium">{match.match}</div>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {match.date && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{match.date}</span>
                            </div>
                          )}
                          {match.time && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{match.time}</span>
                            </div>
                          )}
                          {(match.court || match.courtHidden) && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>{match.courtHidden ? 'Cancha por definir' : `Cancha ${match.court}`}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">
                    No hay partidos programados todavía
                  </p>
                </Card>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
